import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock,
  KeyRound,
  Loader2,
  PlugZap,
  RefreshCw,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import type { MailboxTest, Settings, SettingsInput } from '@afiliados/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtDateTime, fmtRelative } from '@/lib/format';

interface Props {
  form: SettingsInput;
  set: <K extends keyof SettingsInput>(k: K, v: SettingsInput[K]) => void;
  settings: Settings;
  isAdmin: boolean;
  password: string;
  setPassword: (v: string) => void;
  onClearPassword: () => void;
}

const STATUS_KEY = ['settings', 'mailbox'] as const;

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

/**
 * Integração com a caixa de e-mail que recebe os relatórios da BuyGoods: conexão IMAP, filtros
 * e a leitura periódica feita pelo worker. Os campos salvam pelo "Salvar" da página; o teste de
 * conexão e o "Ler agora" agem na hora.
 */
export function MailboxTab({
  form,
  set,
  settings: s,
  isAdmin,
  password,
  setPassword,
  onClearPassword,
}: Props) {
  const qc = useQueryClient();
  const [test, setTest] = useState<MailboxTest | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: settingsApi.mailbox,
    refetchInterval: 5_000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: STATUS_KEY });
  const onError = (fallback: string) => (e: unknown) =>
    toast.error(e instanceof ApiError ? e.message : fallback);

  const testMut = useMutation({
    mutationFn: () =>
      settingsApi.mailboxTest({
        mailboxHost: form.mailboxHost,
        mailboxPort: form.mailboxPort,
        mailboxSecure: form.mailboxSecure,
        mailboxUser: form.mailboxUser,
        mailboxFolder: form.mailboxFolder,
        mailboxSenderFilter: form.mailboxSenderFilter,
        mailboxSubjectFilter: form.mailboxSubjectFilter,
        mailboxSince: form.mailboxSince,
        ...(password ? { mailboxPassword: password } : {}),
      }),
    onSuccess: (r) => {
      setTest(r);
      (r.ok ? toast.success : toast.error)(r.message);
    },
    onError: onError('Falha no teste'),
  });
  const runMut = useMutation({
    mutationFn: settingsApi.mailboxRun,
    onSuccess: () => {
      toast.success('Leitura enfileirada — o worker começa em instantes');
      void invalidate();
    },
    onError: onError('Não foi possível enfileirar'),
  });
  const resetMut = useMutation({
    mutationFn: settingsApi.mailboxReset,
    onSuccess: () => {
      toast.success('Cursor reiniciado');
      void invalidate();
    },
    onError: onError('Não foi possível reiniciar'),
  });

  const st = status.data;
  const busy = !!st && (st.running || st.queued);
  const canRun = isAdmin && !!st?.configured && !busy;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conexão (IMAP)</CardTitle>
          <CardDescription>
            A conta que recebe os relatórios diários da BuyGoods. No Gmail/Google Workspace use host
            imap.gmail.com, porta 993, SSL e uma senha de app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Host</Label>
              <Input
                placeholder="imap.gmail.com"
                value={form.mailboxHost ?? ''}
                onChange={(e) => set('mailboxHost', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Porta</Label>
              <Input
                type="number"
                min={1}
                max={65535}
                value={form.mailboxPort ?? s.mailboxPort}
                onChange={(e) => set('mailboxPort', Number(e.target.value) || 993)}
                disabled={!isAdmin}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Usuário (e-mail)</Label>
              <Input
                placeholder="relatorios@empresa.com"
                value={form.mailboxUser ?? ''}
                onChange={(e) => set('mailboxUser', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
            <div className="flex items-center justify-between self-end rounded-lg border p-3">
              <div>
                <Label>SSL/TLS</Label>
                <p className="text-xs text-muted-foreground">Porta 993.</p>
              </div>
              <Switch
                checked={form.mailboxSecure ?? s.mailboxSecure}
                onCheckedChange={(v) => set('mailboxSecure', v)}
                disabled={!isAdmin}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Senha</Label>
              {s.hasMailboxPassword ? (
                <Badge variant="secondary">configurada</Badge>
              ) : (
                <Badge variant="outline">pendente</Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder={
                  s.hasMailboxPassword ? '•••••••• (cole para substituir)' : 'senha ou senha de app'
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!isAdmin}
              />
              {s.hasMailboxPassword && isAdmin && (
                <Button
                  variant="outline"
                  size="icon"
                  title="Remover senha"
                  onClick={onClearPassword}
                >
                  <KeyRound />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Criptografada no banco (AES-256-GCM).</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Pasta</Label>
              <Input
                placeholder="INBOX"
                value={form.mailboxFolder ?? s.mailboxFolder}
                onChange={(e) => set('mailboxFolder', e.target.value)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ignorar mensagens antes de</Label>
              <Input
                type="date"
                value={form.mailboxSince ?? ''}
                onChange={(e) => set('mailboxSince', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Remetente contém</Label>
              <Input
                placeholder="buygoods.com"
                value={form.mailboxSenderFilter ?? ''}
                onChange={(e) => set('mailboxSenderFilter', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Assunto contém</Label>
              <Input
                placeholder="Affiliates"
                value={form.mailboxSubjectFilter ?? ''}
                onChange={(e) => set('mailboxSubjectFilter', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Só mensagens que passam nos dois filtros são lidas; de cada uma, os anexos .xls, .xlsx e
            .csv viram importações.
          </p>

          {isAdmin && (
            <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending}>
              {testMut.isPending ? <Loader2 className="animate-spin" /> : <PlugZap />} Testar
              conexão
            </Button>
          )}
          {test && (
            <Alert variant={test.ok ? 'default' : 'destructive'}>
              {test.ok ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
              <AlertTitle>{test.ok ? 'Conexão ok' : 'Falha na conexão'}</AlertTitle>
              <AlertDescription className="space-y-1">
                <p>{test.message}</p>
                {test.folders.length > 0 && (
                  <p className="text-xs">
                    Pastas:{' '}
                    <span className="font-mono">
                      {test.folders.slice(0, 12).join(', ')}
                      {test.folders.length > 12 ? '…' : ''}
                    </span>
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Leitura automática</CardTitle>
          <CardDescription>
            O worker lê a pasta de tempos em tempos. Relatório do mesmo período substitui o anterior
            (o mais novo vale); período diferente que cruza um ativo fica "em conflito" para você
            decidir.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Ler automaticamente</Label>
              <p className="text-xs text-muted-foreground">
                Desligado, só o botão "Ler agora" importa. Requer o worker no ar.
              </p>
            </div>
            <Switch
              checked={form.mailboxEnabled ?? s.mailboxEnabled}
              onCheckedChange={(v) => set('mailboxEnabled', v)}
              disabled={!isAdmin}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Intervalo (minutos)</Label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={form.mailboxIntervalMin ?? s.mailboxIntervalMin}
                onChange={(e) => set('mailboxIntervalMin', Number(e.target.value) || 10)}
                disabled={!isAdmin}
              />
            </div>
            <div className="flex items-center justify-between self-end rounded-lg border p-3">
              <div>
                <Label>Marcar como lida</Label>
                <p className="text-xs text-muted-foreground">Depois de processar.</p>
              </div>
              <Switch
                checked={form.mailboxMarkSeen ?? s.mailboxMarkSeen}
                onCheckedChange={(v) => set('mailboxMarkSeen', v)}
                disabled={!isAdmin}
              />
            </div>
          </div>

          <div className="divide-y rounded-lg border text-sm">
            <Row label="Worker">
              {st?.scheduled ? (
                <Badge variant="secondary" className="gap-1">
                  <CheckCircle2 className="size-3 text-success" /> no ar
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <XCircle className="size-3 text-destructive" /> parado
                </Badge>
              )}
            </Row>
            <Row label="Estado">
              {st?.running ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="size-3 animate-spin" /> lendo…
                </span>
              ) : st?.queued ? (
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3" /> na fila
                </span>
              ) : st?.enabled && st.configured ? (
                'aguardando o próximo ciclo'
              ) : (
                'parado'
              )}
            </Row>
            <Row label="Última leitura">
              {st?.lastRunAt
                ? `${fmtDateTime(st.lastRunAt)} (${fmtRelative(st.lastRunAt)})`
                : 'nunca'}
            </Row>
            <Row label="Próxima leitura">{st?.nextRunAt ? fmtRelative(st.nextRunAt) : '—'}</Row>
            <Row label="Relatórios importados por e-mail">{st?.imported ?? 0}</Row>
            <Row label="Cursor (última mensagem lida)">
              <span className="font-mono text-xs">{st?.cursor ?? '—'}</span>
            </Row>
          </div>

          {st?.lastError && (
            <Alert variant="destructive">
              <XCircle className="size-4" />
              <AlertTitle>Última leitura com erro</AlertTitle>
              <AlertDescription className="whitespace-pre-wrap">{st.lastError}</AlertDescription>
            </Alert>
          )}

          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => runMut.mutate()} disabled={!canRun || runMut.isPending}>
                {runMut.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />} Ler agora
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmReset(true)}
                disabled={!st?.cursor || busy || resetMut.isPending}
              >
                <RotateCcw /> Reiniciar cursor
              </Button>
            </div>
          )}
          {!st?.configured && (
            <p className="text-xs text-muted-foreground">
              Preencha e salve a conexão para liberar a leitura.
            </p>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reiniciar o cursor?</AlertDialogTitle>
            <AlertDialogDescription>
              A próxima leitura volta a considerar as mensagens desde "Ignorar mensagens antes de"
              (ou todas, se vazio). Relatórios já importados não entram de novo: o mesmo arquivo é
              reconhecido e ignorado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmReset(false);
                resetMut.mutate();
              }}
            >
              Reiniciar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
